import { useCallback, useRef, useState } from 'react'

// Tracks whether text exceeds `clampLines` lines. Compares the full content
// height (scrollHeight, identical whether the element is collapsed or expanded)
// against the clamp threshold, so it reports correctly in both states and
// re-checks on resize, content edits (admin preview), and after fonts load.
export const useClampOverflow = (clampLines: number) => {
    const [overflowing, setOverflowing] = useState(false)
    const cleanupRef = useRef<(() => void) | null>(null)

    const ref = useCallback(
        (el: HTMLElement | null) => {
            cleanupRef.current?.()
            cleanupRef.current = null
            if (!el) {
                setOverflowing(false)
                return
            }

            const update = () => {
                if (typeof window === 'undefined') return
                const styles = window.getComputedStyle(el)
                const lineHeight =
                    parseFloat(styles.lineHeight) ||
                    parseFloat(styles.fontSize) * 1.2
                setOverflowing(el.scrollHeight > lineHeight * clampLines + 1)
            }
            update()

            const resize =
                typeof ResizeObserver !== 'undefined'
                    ? new ResizeObserver(update)
                    : null
            resize?.observe(el)

            const mutation =
                typeof MutationObserver !== 'undefined'
                    ? new MutationObserver(update)
                    : null
            mutation?.observe(el, {
                childList: true,
                subtree: true,
                characterData: true,
            })

            if (typeof document !== 'undefined' && document.fonts?.ready) {
                document.fonts.ready.then(() => {
                    if (el.isConnected) update()
                })
            }

            cleanupRef.current = () => {
                resize?.disconnect()
                mutation?.disconnect()
            }
        },
        [clampLines],
    )

    return [overflowing, ref] as const
}
